'use client'

import { createContext, useContext, type ComponentType, type ReactNode } from 'react'
import type { SubsSection, SubRowVariant } from '@/msgdeskRuntime/state'

// Panels-area surface seam (25_SHARED_DESKUI_SKELETON.md §5/§6.2): the surface
// supplies its control components (its Legacy/Modern visual picks), its
// template-variant config, and its visual class tokens. The panels skeleton
// reads this context and never imports a surface.

export interface PanelsSkin {
  toolbar: string
  toolbarHide: string
  toolbarMessage: string
  panelAnswers: string
  panelSubmsges: string
  panelTools: string
  rootList: string
  sectionInfo: string
  sectionInfoCount: string
  menuPanel: string
  menuPanelShown: string
  menuPanelHidden: string
  togglePhoto: string
  menuPanelTools: string
  menuList: string
  sectionSwitch: string
  sectionSwitchButton: string
  sectionSwitchButtonActive: string
  sectionSwitchButtonInactive: string
  /** Folder empty-state text ("no subs"). Optional: surfaces adopt the look deliberately. */
  menuEmpty?: string
}

export interface PanelsSurface {
  FoldSubsButton: ComponentType<{ section: 0 | 1; label: string; count?: number }>
  PagingBar: ComponentType<{ section: 0 | 1; currentPage?: number; totalPages?: number; registerContract?: boolean }>
  RefreshSubs: ComponentType<{ section: 0 | 1 }>
  CheckedsInfo: ComponentType<{ section: 0 | 1; mode?: 'msg' | 'drafts' | 'trash' | 'favs'; visual?: 'modern' | 'legacy' }>
  MoreLink: ComponentType<{ section: 0 | 1; moreCount?: number }>
  /** Surface-owned copy for an explicitly empty folder route. */
  menuEmptyLabel: string
  config: {
    mainTemplateVariant: (section: SubsSection) => SubRowVariant
    menuTemplateVariant: (mode: 'drafts' | 'trash' | 'favs', section: SubsSection) => SubRowVariant
  }
  skin: PanelsSkin
}

const PanelsSurfaceContext = createContext<PanelsSurface | null>(null)

export function PanelsSurfaceProvider({ value, children }: { value: PanelsSurface; children: ReactNode }) {
  return <PanelsSurfaceContext.Provider value={value}>{children}</PanelsSurfaceContext.Provider>
}

export function usePanelsSurface(): PanelsSurface {
  const surface = useContext(PanelsSurfaceContext)
  if (!surface) {
    throw new Error('usePanelsSurface: no PanelsSurfaceProvider is mounted above this panels skeleton element')
  }
  return surface
}
